import { useState } from 'react';
import { ArrowLeftRight, X } from '../../lib/icons';
import { toast } from 'sonner';
import { ajouterMouvement, MODES_PAIEMENT } from '../../lib/dataProvider';
import { fmt } from '../shared/badges';
import { PrintDoc } from '../print/PrintDoc';
import { attribuerNumero } from '../../lib/souches';

const inputCls =
  'mt-1.5 h-11 w-full border border-slate-300 bg-slate-50 px-3 font-data text-sm text-slate-900 outline-none transition-colors duration-150 focus:border-slate-900 focus:bg-white';
const label = 'text-xs font-bold uppercase tracking-[0.1em] text-slate-500';

const SENS_RAPIDES = [
  { id: 'coffre-caisse', de: 'esp', vers: 'chq', titre: 'Coffre-fort → Caisse Bascule', hint: 'Dotation du jour' },
  { id: 'caisse-coffre', de: 'chq', vers: 'esp', titre: 'Caisse Bascule → Coffre-fort', hint: 'Remise du soir' },
  { id: 'avance-coursier', de: 'chq', vers: 'crs', titre: 'Caisse Bascule → Caisse Coursier', hint: 'Avance de tournée' },
  { id: 'restitution-coursier', de: 'crs', vers: 'chq', titre: 'Caisse Coursier → Caisse Bascule', hint: 'Restitution du reliquat' },
  { id: 'banque-coffre', de: 'BANQUE', vers: 'esp', titre: 'Banque → Coffre-fort', hint: 'Retrait d’espèces' },
  { id: 'coffre-banque', de: 'esp', vers: 'BANQUE', titre: 'Coffre-fort → Banque', hint: 'Dépôt / versement' },
];

export const TransfertCoffreDialog = ({ comptes, utilisateur, onClose, onDone }) => {
  const aujourdhui = new Date().toISOString().slice(0, 10);
  const internes = comptes.filter((c) => c.type !== 'Banque');
  const banques = comptes.filter((c) => c.type === 'Banque');
  const premiereBanque = banques[0]?.id ?? '';
  const [f, setF] = useState({
    de: 'esp',
    vers: 'chq',
    date: aujourdhui,
    montant: '',
    piece: '',
    motif: '',
    mode: MODES_PAIEMENT[0],
  });
  const [recu, setRecu] = useState(null);

  const source = comptes.find((c) => c.id === f.de);
  const cible = comptes.find((c) => c.id === f.vers);
  const titre = `${source?.nom ?? '—'} → ${cible?.nom ?? '—'}`;
  const montant = Number(f.montant) || 0;
  const optionsComptes = [
    ...internes.map((c) => ({ ...c, groupe: 'Caisses' })),
    ...banques.map((c) => ({ ...c, groupe: 'Banques' })),
  ];
  const resoudre = (id) => (id === 'BANQUE' ? premiereBanque : id);

  const valider = async (e) => {
    e.preventDefault();
    if (f.de === f.vers) return toast.error('Choisissez deux caisses différentes.');
    if (montant <= 0) return toast.error('Saisissez le montant à transférer.');
    if (montant > (source?.solde ?? 0))
      return toast.error(`Solde insuffisant sur « ${source.nom} » : ${fmt(source.solde, 3)} TND.`);
    const piece = f.piece.trim() || `TR-${Date.now().toString().slice(-6)}`;
    const { error } = await ajouterMouvement({
      date: f.date,
      type: 'Transfert',
      compteId: f.de,
      compteCibleId: f.vers,
      categorie: null,
      libelle: f.motif.trim() || titre,
      piece,
      montant,
    });
    if (error) return toast.error(error);
    toast.success(`Transfert de ${fmt(montant, 3)} TND enregistré (aucune dépense comptabilisée)`);
    setRecu({
      piece,
      date: f.date,
      montant,
      sens: titre,
      motif: f.motif.trim() || '—',
      mode: f.mode,
      sourceNom: source.nom,
      cibleNom: cible.nom,
      soldeSourceAvant: source.solde,
      soldeSourceApres: source.solde - montant,
      soldeCibleAvant: cible.solde,
      soldeCibleApres: cible.solde + montant,
    });
    onDone?.();
  };

  if (recu)
    return (
      <PrintDoc
        type="Reçu de transfert de trésorerie"
        numero={attribuerNumero('recu_paiement', recu.piece)}
        date={recu.date}
        tiers={`${titre} — opéré par ${utilisateur || '—'}`}
        lignes={[
          ['Sens du transfert', recu.sens],
          ['Compte débité', `${recu.sourceNom} — solde avant ${fmt(recu.soldeSourceAvant, 3)} TND / après ${fmt(recu.soldeSourceApres, 3)} TND`],
          ['Compte crédité', `${recu.cibleNom} — solde avant ${fmt(recu.soldeCibleAvant, 3)} TND / après ${fmt(recu.soldeCibleApres, 3)} TND`],
          ['Nature des fonds', recu.mode],
          ['Motif', recu.motif],
          ['N° de pièce', recu.piece],
        ]}
        totaux={[['Montant transféré', `${fmt(recu.montant, 3)} TND`]]}
        mentions="Reçu à signer par les deux parties : les fonds ont été physiquement remis et comptés en présence des signataires."
        signatures={[`Remis par — ${recu.sourceNom}`, `Reçu par — ${recu.cibleNom}`]}
        onClose={() => {
          setRecu(null);
          onClose();
        }}
      />
    );

  return (
    <div data-testid="transfert-coffre-overlay" className="fixed inset-0 z-[65] flex items-center justify-center bg-slate-900/60 p-4">
      <form onSubmit={valider} data-testid="transfert-coffre-form" className="max-h-[92vh] w-full max-w-lg overflow-y-auto border border-slate-200 bg-white">
        <div className="flex items-start justify-between border-b border-slate-200 px-5 py-4">
          <div>
            <h2 className="flex items-center gap-2 font-heading text-lg font-black tracking-tight text-slate-900">
              <ArrowLeftRight size={18} /> Transfert de trésorerie
            </h2>
            <p className="font-data text-[11px] uppercase tracking-[0.1em] text-slate-500">Banques ↔ Coffre-fort ↔ Caisse Bascule ↔ Caisse Coursier</p>
          </div>
          <button type="button" data-testid="transfert-coffre-close" onClick={onClose} className="border border-slate-200 p-2 text-slate-500 hover:bg-slate-900 hover:text-white">
            <X size={16} />
          </button>
        </div>

        <div className="space-y-4 px-5 py-5">
          <div className="grid grid-cols-1 gap-px bg-slate-200 sm:grid-cols-2">
            {SENS_RAPIDES.filter((s) => comptes.some((c) => c.id === resoudre(s.de)) && comptes.some((c) => c.id === resoudre(s.vers))).map((s) => {
              const de = resoudre(s.de), vers = resoudre(s.vers);
              const actif = f.de === de && f.vers === vers;
              return (
                <button
                  key={s.id}
                  type="button"
                  data-testid={`sens-${s.id}`}
                  onClick={() => setF((p) => ({ ...p, de, vers }))}
                  className={`px-3 py-3 text-left transition-colors duration-150 ${actif ? 'bg-slate-900 text-white' : 'bg-white hover:bg-slate-50'}`}
                >
                  <p className="text-sm font-bold">{s.titre}</p>
                  <p className={`text-xs ${actif ? 'text-slate-300' : 'text-slate-500'}`}>{s.hint}</p>
                </button>
              );
            })}
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className={label}>Compte source (débité)</label>
              <select data-testid="transfert-de" value={f.de} onChange={(e) => setF({ ...f, de: e.target.value })} className={inputCls}>
                {['Caisses', 'Banques'].map((g) => (
                  <optgroup key={g} label={g}>
                    {optionsComptes.filter((c) => c.groupe === g).map((c) => (
                      <option key={c.id} value={c.id}>{`${c.nom} — ${fmt(c.solde, 3)} TND`}</option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>
            <div>
              <label className={label}>Compte destination (crédité)</label>
              <select data-testid="transfert-vers" value={f.vers} onChange={(e) => setF({ ...f, vers: e.target.value })} className={inputCls}>
                {['Caisses', 'Banques'].map((g) => (
                  <optgroup key={g} label={g}>
                    {optionsComptes.filter((c) => c.groupe === g).map((c) => (
                      <option key={c.id} value={c.id}>{`${c.nom} — ${fmt(c.solde, 3)} TND`}</option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>
          </div>

          <div className="border-l-2 border-slate-900 bg-slate-50 px-3 py-2 font-data text-xs text-slate-700">
            Disponible sur « {source?.nom} » :{' '}
            <span data-testid="transfert-solde-source" className="font-semibold">{fmt(source?.solde ?? 0, 3)} TND</span>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className={label}>Date</label>
              <input data-testid="transfert-date" type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} className={inputCls} />
            </div>
            <div>
              <label className={label}>Montant (TND)</label>
              <input data-testid="transfert-montant" type="number" step="0.001" min="0" value={f.montant} onChange={(e) => setF({ ...f, montant: e.target.value })} className={inputCls} />
            </div>
            <div>
              <label className={label}>Nature des fonds</label>
              <select data-testid="transfert-mode" value={f.mode} onChange={(e) => setF({ ...f, mode: e.target.value })} className={inputCls}>
                {MODES_PAIEMENT.map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={label}>N° de pièce</label>
              <input data-testid="transfert-piece" value={f.piece} onChange={(e) => setF({ ...f, piece: e.target.value })} className={inputCls} placeholder="auto si vide" />
            </div>
          </div>

          <div>
            <label className={label}>Motif</label>
            <input data-testid="transfert-motif" value={f.motif} onChange={(e) => setF({ ...f, motif: e.target.value })} className={inputCls} placeholder="ex. dotation du jour pour paiement des agriculteurs" />
          </div>

          {montant > 0 && (
            <div data-testid="transfert-apercu" className="grid grid-cols-2 gap-px bg-slate-200 font-data text-xs">
              <div className="bg-white px-3 py-2">
                <p className="text-slate-500">{source?.nom} après</p>
                <p className="tabular text-sm font-semibold text-slate-900">{fmt((source?.solde ?? 0) - montant, 3)}</p>
              </div>
              <div className="bg-white px-3 py-2">
                <p className="text-slate-500">{cible?.nom} après</p>
                <p className="tabular text-sm font-semibold text-slate-900">{fmt((cible?.solde ?? 0) + montant, 3)}</p>
              </div>
            </div>
          )}
        </div>

        <div className="flex gap-3 border-t border-slate-200 px-5 py-4">
          <button type="submit" data-testid="transfert-valider" className="h-11 flex-1 bg-slate-900 font-heading text-sm font-bold uppercase tracking-[0.08em] text-white transition-colors duration-150 hover:bg-slate-700">
            Valider et imprimer le reçu
          </button>
          <button type="button" onClick={onClose} className="h-11 border border-slate-300 px-5 text-sm font-semibold text-slate-700 hover:bg-slate-100">
            Annuler
          </button>
        </div>
      </form>
    </div>
  );
};
